import { defineComponent as _defineComponent } from 'vue'
import { AlgoliaDfrEntry, DfrEmployee, DfrStatus } from '@newmoon-org/types';
import dayjs from 'dayjs';
import { pick } from 'lodash';
import { ref, computed } from 'vue';

import DfrService from '@/service/dfr.service';

import { useStore } from '@/store';

enum BatchUpdateType {
  Approve = 'approve',
  HrApprove = 'hr_approve',
}

type ApproveUpdate = {
  status: DfrStatus;
  finalizedBy: DfrEmployee;
};

type HrApproveUpdate = {
  hasHrApproval: boolean;
  hrFinalizedBy: DfrEmployee;
};

type BatchUpdate = ApproveUpdate | HrApproveUpdate;


export default /*#__PURE__*/_defineComponent({
  __name: 'DfrBatchUpdateModal',
  emits: ['close'],
  setup(__props, { emit: emits }) {


const store = useStore();
const isLoading = ref<boolean>(false);
const dfrs = ref<AlgoliaDfrEntry[]>(store.getters['dfr/getSelected']);
const hasHrInput = computed(() => dfrs.value.some(dfr => dfr.hasHrApproval));

const user = store.getters['auth/user'];

function mapDfrs(type: BatchUpdateType): Array<[string, BatchUpdate]> {
  if (type === BatchUpdateType.Approve) {
    return dfrs.value.map(dfr => [
      dfr.objectID,
      {
        status: DfrStatus.approved,
        finalizedBy: {
          ...pickEmployee(user.employee),
          date: new Date(),
        },
      },
    ]);
  } else if (type === BatchUpdateType.HrApprove) {
    return dfrs.value.map(dfr => [
      dfr.objectID,
      {
        hasHrApproval: true,
        hrFinalizedBy: {
          ...pickEmployee(user.employee),
          date: new Date(),
        },
      },
    ]);
  }
  throw new Error('Unknown type');
}

async function batchUpdate(type: BatchUpdateType) {
  try {
    isLoading.value = true;
    // update all the dfrs AND wait for the algolia event to be done
    await Promise.all([
      ...mapDfrs(type).map(tuple => {
        const [id, dfr] = tuple;
        return DfrService.update(id, { ...dfr, updatedAt: new Date() }, true);
      }),
      DfrService.waitForAlgoliaEvent(),
    ]);
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
    emits('close');
    await store.dispatch('dfr/clearSelected');
  }
}

function pickEmployee(e): DfrEmployee {
  return pick(e, [
    'id',
    'firstName',
    'lastName',
    'email',
    'code',
    'manager',
    'isForeman',
    'workflowFunction',
    'payrollSystem',
  ]);
}

function isSelfUpdate(dfr: AlgoliaDfrEntry): boolean {
  return dfr.reportedBy?.id === dfr.employee?.id;
}

function getDuration(item) {
  const startDate = new Date(item.startDate_timestamp);
  const endDate = new Date(item.endDate_timestamp);

  if (startDate && endDate) {
    // @ts-ignore
    const difference = (endDate - startDate) / 1000 / 3600;
    const label = difference === 1 ? 'hour' : 'hours';
    const [, decimals] = difference.toString().split('.');
    const numberOfDigits = decimals?.length >= 2 ? 2 : decimals?.length == 1 ? 1 : 0;
    return `${difference.toFixed(numberOfDigits)} ${label}`;
  }
  return '';
}

function formatDate(item) {
  if (!item.startDate_timestamp) return 'Unknown';
  const startDate = new Date(item.startDate_timestamp);
  const start = dayjs(startDate);
  return `${start.format('M/D/YYYY')}`;
}

function formatTime(item) {
  const startDate = new Date(item.startDate_timestamp);
  const endDate = new Date(item.endDate_timestamp);
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return `${start.format('h:mm A')} - ${end.format('h:mm A')}`;
}

function mapStatus(status) {
  const statusMap = {
    approved: 'APPROVED',
    new: 'NEW',
    needs_revision: 'NEEDS REVISION',
    denied: 'DENIED',
  };
  return statusMap[status];
}

return { __sfc: true,emits, store, isLoading, dfrs, hasHrInput, user, BatchUpdateType, mapDfrs, batchUpdate, pickEmployee, isSelfUpdate, getDuration, formatDate, formatTime, mapStatus }
}

})